import JSZip from "jszip";
import { downloadFileLink } from "@/lib/utils/matterFile";

export type ZipDownloadItem = { name: string } & (
  | { children: ZipDownloadItem[] }
  | { getUrl: () => Promise<string> }
);

const getUniqueZipFileName = (name: string, usedNames: Set<string>) => {
  const sanitized = name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, "_");
  const safeName =
    sanitized && sanitized !== "." && sanitized !== ".." ? sanitized : "file";
  const dotIndex = safeName.lastIndexOf(".");
  const base = dotIndex > 0 ? safeName.slice(0, dotIndex) : safeName;
  const extension = dotIndex > 0 ? safeName.slice(dotIndex) : "";
  let candidate = safeName;
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    candidate = `${base} (${suffix})${extension}`;
    suffix += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
};

export async function downloadZip(
  items: ZipDownloadItem[],
  archiveName: string,
) {
  const zip = new JSZip();
  const addItems = async (children: ZipDownloadItem[], directory: JSZip) => {
    const usedNames = new Set<string>();
    for (const item of children) {
      const name = getUniqueZipFileName(item.name, usedNames);
      if ("children" in item) {
        const folder = directory.folder(name);
        if (!folder) throw new Error("Unable to create the ZIP folder.");
        await addItems(item.children, folder);
      } else {
        const response = await fetch(await item.getUrl());
        if (!response.ok) throw new Error(`Failed to download ${item.name}.`);
        directory.file(name, await response.blob());
      }
    }
  };

  await addItems(items, zip);
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  try {
    downloadFileLink(archiveName, url);
  } finally {
    URL.revokeObjectURL(url);
  }
}
